import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail, ChevronLeft, ShieldCheck, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { adminLogin, mfaVerify } from "@/api";
import { getErrorMessage } from "@/lib/errors";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challengeToken, setChallengeToken] = useState(null);
  const [code, setCode] = useState("");

  const goToDashboard = (rol) => {
    if (rol === "tecnico_ayuntamiento") { navigate("/admin/ayuntamiento"); return; }
    if (rol === "punto_limpio_staff") { navigate("/admin/punto-limpio"); return; }
    navigate("/admin/dashboard");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await adminLogin(email.trim().toLowerCase(), password);
      if (data.mfa_required) {
        setChallengeToken(data.challenge_token);
        return;
      }
      localStorage.setItem("sfh_admin_token", data.access_token);
      localStorage.setItem("sfh_admin_session", JSON.stringify({ email: data.email, nombre: data.nombre, rol: data.rol }));
      toast.success(`Bienvenido, ${data.nombre}`);
      goToDashboard(data.rol);
    } catch (err) {
      setError(getErrorMessage(err, "Credenciales incorrectas."));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await mfaVerify(challengeToken, code);
      localStorage.setItem("sfh_admin_token", data.access_token);
      localStorage.setItem("sfh_admin_session", JSON.stringify({ email: data.email, nombre: data.nombre, rol: data.rol }));
      toast.success(`Bienvenido, ${data.nombre}`);
      goToDashboard(data.rol);
    } catch (err) {
      setError(getErrorMessage(err, "Código incorrecto."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-municipal flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-municipal-dark via-municipal to-municipal-light opacity-90" />
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/80 mb-6 hover:text-white">
          <ChevronLeft className="w-4 h-4" /> Volver al portal
        </Link>
        <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-10">
          <div className="w-14 h-14 rounded-xl bg-municipal/10 flex items-center justify-center mb-5">
            {challengeToken ? <KeyRound className="w-7 h-7 text-municipal" /> : <ShieldCheck className="w-7 h-7 text-municipal" />}
          </div>

          {challengeToken ? (
            <>
              <h1 className="font-heading font-bold text-2xl text-municipal mb-1">Verificación en dos pasos</h1>
              <p className="text-gray-500 text-sm mb-8">Introduce el código de 6 dígitos de tu aplicación de autenticación.</p>
              <form onSubmit={handleVerifyMfa} className="space-y-5">
                <Input
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  className="text-center text-lg tracking-widest"
                  data-testid="mfa-code-input"
                  required
                />
                {error && <p className="text-sm text-red-500" data-testid="mfa-error">{error}</p>}
                <Button type="submit" disabled={loading || code.length !== 6} className="w-full bg-municipal hover:bg-municipal-dark text-white h-11" data-testid="mfa-verify-submit">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Verificar
                </Button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-heading font-bold text-2xl text-municipal mb-1">Acceso administración</h1>
              <p className="text-gray-500 text-sm mb-8">Panel interno Ayuntamiento SFH / Valoriza</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className="mb-1.5 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@sanfernandodehenares.es"
                      className="pl-9"
                      data-testid="admin-email-input"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="mb-1.5 block">Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-9"
                      data-testid="admin-password-input"
                      required
                    />
                  </div>
                </div>
                {error && <p className="text-sm text-red-500" data-testid="admin-login-error">{error}</p>}
                <Button type="submit" disabled={loading} className="w-full bg-municipal hover:bg-municipal-dark text-white h-11" data-testid="admin-login-submit">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Iniciar sesión
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

